import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useProfile } from "./useAuth";

interface LicenceStatus {
  loading: boolean;
  isValid: boolean;
  isExpired: boolean;
  daysUntilExpiry: number | null;
  showWarning: boolean; // true if expiry < 30 days
  features: Record<string, boolean>;
  maxUsers: number | null;
  maxDevices: number | null;
}

const DEFAULT_STATUS: LicenceStatus = {
  loading: true,
  isValid: true,
  isExpired: false,
  daysUntilExpiry: null,
  showWarning: false,
  features: {},
  maxUsers: null,
  maxDevices: null,
};

export function useLicence(): LicenceStatus {
  const { user } = useAuth();
  const { profile } = useProfile();
  const [status, setStatus] = useState<LicenceStatus>(DEFAULT_STATUS);

  useEffect(() => {
    if (!user || !profile?.organization_id) {
      setStatus((prev) => ({ ...prev, loading: false }));
      return;
    }

    (async () => {
      try {
        // Get the licence linked to the organization
        const { data: org } = await supabase
          .from("organizations")
          .select("licence_id")
          .eq("id", profile.organization_id)
          .single();

        if (!org?.licence_id) {
          // No licence linked yet — allow access (onboarding phase)
          setStatus({
            loading: false,
            isValid: true,
            isExpired: false,
            daysUntilExpiry: null,
            showWarning: false,
            features: { montage_social: true, montage_consolide: false, pointage_efi_pdf: false },
            maxUsers: null,
            maxDevices: null,
          });
          return;
        }

        const { data: licence } = await supabase
          .from("licences")
          .select("status, valid_until, features, max_users, max_devices")
          .eq("id", org.licence_id)
          .single();

        if (!licence) {
          setStatus({ ...DEFAULT_STATUS, loading: false, isValid: false });
          return;
        }

        const validUntil = new Date(licence.valid_until);
        const now = new Date();
        const msPerDay = 1000 * 60 * 60 * 24;
        const daysUntilExpiry = Math.ceil((validUntil.getTime() - now.getTime()) / msPerDay);
        const isExpired = licence.status === "expired" || daysUntilExpiry <= 0;
        const isSuspended = licence.status === "suspended";

        setStatus({
          loading: false,
          isValid: !isExpired && !isSuspended && licence.status !== "suspended",
          isExpired,
          daysUntilExpiry: Math.max(0, daysUntilExpiry),
          showWarning: !isExpired && daysUntilExpiry <= 30,
          features: (licence.features as Record<string, boolean>) || {},
          maxUsers: licence.max_users,
          maxDevices: licence.max_devices,
        });
      } catch (err) {
        console.error("useLicence error:", err);
        setStatus((prev) => ({ ...prev, loading: false }));
      }
    })();
  }, [user, profile?.organization_id]);

  return status;
}
